<script setup lang="ts">
import type { VTableFilterConfig } from "../../composables/Table/useTableFilters";
import type { ActiveFilter } from "../../types/TableFilter.types";
import type { VTableFilterPreviewCount } from "./VTable.types";
import { useDebounceFn } from "@vueuse/core";
import { computed, onBeforeUnmount, onMounted, reactive, ref, useId, watch } from "vue";
import { useI18n } from "vue-i18n";
import { useFilterSummary } from "../../composables/Table/useFilterSummary";
import { defaultConditionOf, isFilterSet, useTableFilters } from "../../composables/Table/useTableFilters";
import VButton from "../Button/VButton.vue";
import VIcon from "../Icon/VIcon.vue";
import VTableFilterEditor from "./VTableFilterEditor.vue";

defineOptions({
  name: "VTableFilterPanel",
});

/**
 * The filter panel of a VTable (340px): every filter as a row with its current value, unfolding inline to edit it.
 * Changes stay a draft until Toepassen; Annuleren drops them. With `previewCount` the apply button counts the
 * matches of the draft ("Toon 6 gebruikers"); per-option counts are a later phase.
 */
const {
  previewCount = undefined,
  itemLabel,
  itemsLabel,
} = defineProps<{
  previewCount?: VTableFilterPreviewCount
  itemLabel: string
  itemsLabel: string
}>();

const emit = defineEmits<{
  close: []
}>();

type FilterDraft = Pick<ActiveFilter, "condition" | "value">;

const { t } = useI18n();
const { filterableFields, getFilterForField, replaceFilters, buildParams, optionsOf } = useTableFilters();
const { summarize } = useFilterSummary();
const uid = useId();

function emptyDraft(config: VTableFilterConfig): FilterDraft {
  return { condition: defaultConditionOf(config), value: null };
}

function initialDraft(config: VTableFilterConfig): FilterDraft {
  const active = getFilterForField(config.field);
  if (!active) {
    return emptyDraft(config);
  }
  return {
    condition: active.condition,
    value: Array.isArray(active.value) ? [...active.value] as ActiveFilter["value"] : active.value,
  };
}

const drafts = reactive<Record<string, FilterDraft>>(
  Object.fromEntries(filterableFields.map(config => [config.field, initialDraft(config)])),
);

const firstActive = filterableFields.find(config => isFilterSet(drafts[config.field]));
const expanded = ref<string | null>(firstActive?.field ?? filterableFields[0]?.field ?? null);

function toggle(field: string) {
  expanded.value = expanded.value === field ? null : field;
}

function rowId(field: string): string {
  return `${uid}-${field.replace(/\W+/g, "_")}`;
}

const draftFilters = computed<ActiveFilter[]>(() =>
  filterableFields
    .map(config => ({ field: config.field, ...drafts[config.field] }))
    .filter(isFilterSet),
);

const draftEmpty = computed(() => draftFilters.value.length === 0);

function clearDraft() {
  filterableFields.forEach((config) => {
    drafts[config.field] = emptyDraft(config);
  });
}

function apply() {
  replaceFilters(draftFilters.value);
  emit("close");
}

function cancel() {
  emit("close");
}

// Live count of the draft ------------------------------------------------------------------------------------------
const count = ref<number | null>(null);
const counting = ref(false);
let controller: AbortController | null = null;

async function runCount() {
  if (!previewCount) {
    return;
  }
  controller?.abort();
  const own = new AbortController();
  controller = own;
  counting.value = true;
  try {
    const result = await previewCount(buildParams(draftFilters.value), own.signal);
    if (controller === own) {
      count.value = result;
    }
  } catch {
    // A failed or aborted count leaves the plain apply button; the filter itself still works.
    if (controller === own) {
      count.value = null;
    }
  } finally {
    if (controller === own) {
      counting.value = false;
    }
  }
}

const scheduleCount = useDebounceFn(runCount, 250);

onMounted(() => {
  void runCount();
});

watch(draftFilters, () => {
  if (previewCount) {
    counting.value = true;
    void scheduleCount();
  }
}, { deep: true });

onBeforeUnmount(() => {
  controller?.abort();
});

const applyLabel = computed(() => {
  if (!previewCount || count.value === null) {
    return t("filters.apply");
  }
  if (count.value === 0) {
    return t("filters.show_none", { items: itemsLabel });
  }
  return t("filters.show_count", { count: count.value, item: itemLabel, items: itemsLabel }, count.value);
});

const noMatches = computed(() => previewCount !== undefined && count.value === 0 && !counting.value);

function onKeydown(event: KeyboardEvent) {
  if (event.key === "Escape") {
    event.stopPropagation();
    cancel();
  }
}
</script>

<template>
  <div
    class="flex w-[340px] max-w-[calc(100vw-32px)] flex-col text-sm text-(--text)"
    role="dialog"
    :aria-labelledby="`${uid}-title`"
    data-testid="table-filter-panel"
    @keydown="onKeydown"
  >
    <div class="flex items-center justify-between gap-3 pt-3 pr-3 pb-2.5 pl-4">
      <h2 :id="`${uid}-title`" class="m-0 text-base font-bold">
        {{ t("filters.filters") }}
      </h2>
      <button
        type="button"
        class="h-7 cursor-pointer rounded-full px-2 text-xs font-medium text-danger-700 hover:bg-gray-100 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-primary-500 disabled:pointer-events-none disabled:opacity-40 dark:text-danger-400 dark:hover:bg-dark-700"
        :disabled="draftEmpty"
        data-testid="table-filter-panel-clear"
        @click="clearDraft"
      >
        {{ t("filters.clear_all") }}
      </button>
    </div>

    <div class="mx-3 max-h-[min(60vh,520px)] overflow-y-auto rounded-xl border border-gray-300 dark:border-dark-600">
      <div
        v-for="(config, index) in filterableFields"
        :key="config.field"
        :class="{ 'border-t border-gray-200 dark:border-dark-600': index > 0 }"
      >
        <button
          :id="rowId(config.field)"
          type="button"
          class="flex min-h-11 w-full cursor-pointer items-center gap-2.5 bg-white px-3 py-2.5 text-left hover:bg-gray-50 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary-500 dark:bg-dark-800 dark:hover:bg-dark-700"
          :class="{ 'bg-gray-50! dark:bg-dark-700!': expanded === config.field }"
          :aria-expanded="expanded === config.field"
          :aria-controls="`${rowId(config.field)}-body`"
          @click="toggle(config.field)"
        >
          <span :id="`${rowId(config.field)}-name`" class="shrink-0 font-medium">{{ config.label }}</span>
          <span
            class="min-w-0 flex-1 truncate text-right text-[13px]"
            :class="isFilterSet(drafts[config.field]) ? 'font-medium text-primary-700 dark:text-primary-400' : 'text-gray-600 dark:text-gray-400'"
          >
            {{ summarize(config, drafts[config.field], optionsOf(config.field)) }}
          </span>
          <VIcon
            name="chevron-right"
            class="shrink-0 text-xs transition-transform motion-reduce:transition-none"
            :class="expanded === config.field ? 'rotate-90 text-primary-500' : 'text-gray-500'"
            aria-hidden="true"
          />
        </button>
        <div
          v-if="expanded === config.field"
          :id="`${rowId(config.field)}-body`"
          class="bg-gray-50 px-2 pt-0.5 pb-2.5 dark:bg-dark-700"
        >
          <VTableFilterEditor
            v-model="drafts[config.field]"
            :config="config"
            :label-id="`${rowId(config.field)}-name`"
          />
        </div>
      </div>
    </div>

    <div class="mt-3 flex items-center justify-end gap-2 rounded-b-xl border-t border-gray-100 bg-gray-50/60 p-3 dark:border-dark-700 dark:bg-dark-800">
      <VButton variant="gray" rounded @click="cancel">
        {{ t("filters.cancel") }}
      </VButton>
      <VButton
        rounded
        :loading="counting && count === null"
        :disabled="noMatches"
        data-testid="table-filter-panel-apply"
        @click="apply"
      >
        {{ applyLabel }}
      </VButton>
    </div>
  </div>
</template>
